import { useState, useEffect, useCallback } from 'react';

// IndexedDB types for analytics data
interface StoredAnalyticsData {
  id?: number;
  date: string;
  project_id: string;
  actual_output_mwh: number;
  expected_output_mwh: number;
  peak_power_mw: number;
  nameplate_capacity_mw: number;
  cloud_cover_avg: number;
  sun_hours: number;
  temperature_avg: number;
  capacity_factor: number;
  homes_powered: number;
  stored_at: string;
}

interface AnalyticsSummary {
  total_records: number;
  date_range: {
    earliest: string;
    latest: string;
  };
  last_updated: string;
}

class AnalyticsDB {
  private db: IDBDatabase | null = null;
  private readonly dbName = 'SolarAnalytics';
  private readonly version = 1;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('Analytics DB initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('daily_metrics')) {
          const store = db.createObjectStore('daily_metrics', { keyPath: 'id', autoIncrement: true });
          store.createIndex('date_project', ['date', 'project_id'], { unique: true });
          store.createIndex('date', 'date', { unique: false });
          store.createIndex('project', 'project_id', { unique: false });
        }
        if (!db.objectStoreNames.contains('summary')) {
          db.createObjectStore('summary', { keyPath: 'key' });
        }
      };
    });
  }

  async storeDailyMetrics(data: StoredAnalyticsData[]): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['daily_metrics'], 'readwrite');
      const store = transaction.objectStore('daily_metrics');

      let completed = 0;
      const total = data.length;

      if (total === 0) {
        resolve();
        return;
      }

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);

      data.forEach(item => {
        const request = store.put({
          ...item,
          stored_at: new Date().toISOString()
        });

        request.onsuccess = () => {
          completed++;
          if (completed === total) {
            // Update summary
            this.updateSummary();
          }
        };
      });
    });
  }

  async getDailyMetrics(
    startDate?: string,
    endDate?: string,
    projectId: string = 'east-pye'
  ): Promise<StoredAnalyticsData[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['daily_metrics'], 'readonly');
      const store = transaction.objectStore('daily_metrics');
      const index = store.index('project');
      const request = index.openCursor(IDBKeyRange.only(projectId));

      const results: StoredAnalyticsData[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const item = cursor.value as StoredAnalyticsData;
          if ((!startDate || item.date >= startDate) &&
              (!endDate || item.date <= endDate)) {
            results.push(item);
          }
          cursor.continue();
        } else {
          // Sort by date
          results.sort((a, b) => a.date.localeCompare(b.date));
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async getSummary(): Promise<AnalyticsSummary | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['summary'], 'readonly');
      const store = transaction.objectStore('summary');
      const request = store.get('analytics_summary');

      request.onsuccess = () => {
        resolve(request.result || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  private async updateSummary(): Promise<void> {
    try {
      const allData = await this.getDailyMetrics();
      if (allData.length === 0) return;

      const summary: AnalyticsSummary = {
        total_records: allData.length,
        date_range: {
          earliest: allData[0].date,
          latest: allData[allData.length - 1].date
        },
        last_updated: new Date().toISOString()
      };

      const transaction = this.db!.transaction(['summary'], 'readwrite');
      const store = transaction.objectStore('summary');
      store.put({ key: 'analytics_summary', ...summary });
    } catch (error) {
      console.error('Failed to update summary:', error);
    }
  }

  async clearData(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['daily_metrics', 'summary'], 'readwrite');

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);

      const metricsStore = transaction.objectStore('daily_metrics');
      metricsStore.clear();

      const summaryStore = transaction.objectStore('summary');
      summaryStore.clear();
    });
  }
}

// Global DB instance
const analyticsDB = new AnalyticsDB();

// React hook for analytics data management
export function useLocalAnalytics() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);

  useEffect(() => {
    const initDB = async () => {
      try {
        await analyticsDB.init();
        const dbSummary = await analyticsDB.getSummary();
        setSummary(dbSummary);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize analytics DB:', error);
        setIsInitialized(true); // Still mark as initialized to prevent infinite loading
      }
    };

    initDB();
  }, []);

  const storeData = useCallback(async (data: StoredAnalyticsData[]) => {
    try {
      await analyticsDB.storeDailyMetrics(data);
      const updatedSummary = await analyticsDB.getSummary();
      setSummary(updatedSummary);
    } catch (error) {
      console.error('Failed to store analytics data:', error);
      throw error;
    }
  }, []);

  const getData = useCallback(async (
    startDate?: string,
    endDate?: string,
    projectId: string = 'east-pye'
  ): Promise<StoredAnalyticsData[]> => {
    try {
      return await analyticsDB.getDailyMetrics(startDate, endDate, projectId);
    } catch (error) {
      console.error('Failed to get analytics data:', error);
      return [];
    }
  }, []);

  const clearData = useCallback(async () => {
    try {
      await analyticsDB.clearData();
      setSummary(null);
    } catch (error) {
      console.error('Failed to clear analytics data:', error);
      throw error;
    }
  }, []);

  const refreshSummary = useCallback(async () => {
    try {
      const updatedSummary = await analyticsDB.getSummary();
      setSummary(updatedSummary);
    } catch (error) {
      console.error('Failed to refresh summary:', error);
    }
  }, []);

  return {
    isInitialized,
    summary,
    storeData,
    getData,
    clearData,
    refreshSummary
  };
}
